import type { ProcessingJobStatusRecord, UploadRecord } from "./driver-api.ts";
import type { QueuedUpload } from "./offline-queue.ts";
import type { CheckProps } from "./ui/PhoneShell.tsx";
import type { ChipClass } from "./shift-readiness.ts";

/**
 * `Document upload` — the photographs leaving this handset, and the check waiting for them.
 *
 * The design's screen, value for value from `15 WP14 Driver Onboarding and Earnings.dc.html`:
 *
 *     upload:{l:'Document upload',art:'scan',on:0,chip:['unknown','uploading 2 of 3'],
 *       title:'Uploading your permit',
 *       sub:'Keep the app open. On weak signal we resume where we left off.',
 *       checks:[['ok','image','Front','Uploaded'],
 *               ['wait','image','Back','64% · resumes if interrupted'],
 *               ['wait','image','Selfie with permit','Waiting']],
 *       note:['pending','Photos are checked for blur and glare before upload (FIL-03), so you
 *             won\\'t have to redo them later.'],
 *       btns:[]}
 *
 * `tests/contract/mobile-screen-support.test.ts` had it **blocked** on one thing:
 *
 *   > `Photos are checked for blur and glare before upload (FIL-03)` — only
 *   > `GET /internal/v1/tenants/{tenantId}/jobs/{jobId}` exists, and the gateway does not expose
 *   > `/internal/v1`
 *
 * That is now published on `/v1` under `media-processing:status:read`, narrowed to a status and a
 * transformation — `docs/OPEN_WORK.md` item 2, in ADR 0025's and ADR 0028's shape. ADR 0029 is why
 * it had to be published rather than routed: the gateway refuses the workload prefix outright, so
 * the sentence above was not unfinished work but a deliberate unreachability.
 *
 * ## Two halves, and only one of them is a platform read
 *
 * **The three rows are this handset's.** What has left the phone and what has not is device state,
 * exactly as `6 Offline queue`'s three items are: the record lives in `localStorage`, the captured
 * bytes live in memory beside it, and `src/offline-queue.ts` owns both. Nothing on the platform
 * publishes "what this handset has not managed to send yet" and nothing could.
 *
 * **The note is FIL-03's.** The design states a policy — that a photograph is checked before it
 * counts — and a policy printed without checking is a claim. So the sentence is drawn when FIL-03
 * confirms a job for this capture is queued or running, and says something different when FIL-03
 * says the check failed. That is the whole reason this screen reads a second service.
 *
 * ## The two values with no source, drawn as absences rather than invented
 *
 * **`64%`.** The design's second row draws a byte-level percentage. A `fetch` PUT to FIL-02's signed
 * URL reports no progress — `writeObject` in `src/driver-api.ts` is one request with a `Blob` body —
 * so there is no number. The row draws the half of the design's sentence that is true of this
 * build, `resumes if interrupted`, which ADR 0025 §1 is what makes true: the queue holds the
 * `uploadId` and the aggregate version, so a resumed attempt completes rather than re-initiating.
 * Recorded as Q-142.
 *
 * **`permit`.** The design's headline names what the photographs are of, and nothing on this screen
 * publishes it: FIL-02 takes a content type, a size and a digest; FIL-03 takes a transformation;
 * and the upload cannot be attached to a DRV-03 document at all, because that write needs a scope a
 * courier does not hold — the absence `7 Document expiring`'s `Upload renewed permit` already
 * carries. DRV-03's own `type` is a free string with no published vocabulary, so taking one from the
 * address would be this handset inventing the value rather than being told it. So the title stops
 * where the platform's knowledge stops and carries the design's own `unknown` mark after it, which
 * is `8 Proof`'s treatment of its recipient. Recorded as Q-142.
 */

/** The scope this screen spends beyond FIL-02's, named once so the derived client grant sees it. */
export const JOB_STATUS_SCOPE = "media-processing:status:read";

/** FIL-02's own scope, which the three rows and the resume both go through. */
export const UPLOAD_SELF_SCOPE = "uploads:self";

/** The design's headline, up to the word the platform cannot supply. */
export const UPLOADING_TITLE = "Uploading your";

/** Said after the headline, in the design's `unknown` mark. */
export const UPLOAD_SUBJECT_ABSENCE =
  "Nothing published says what these photographs are of: FIL-02 takes a content type, a size and a digest, and this upload cannot be attached to a DRV-03 document from a courier's token.";

/** The design's sub-line, word for word. */
export const KEEP_THE_APP_OPEN = "Keep the app open. On weak signal we resume where we left off.";

/** The design's note, word for word. */
export const BLUR_AND_GLARE_NOTE =
  "Photos are checked for blur and glare before upload (FIL-03), so you won't have to redo them later.";

/** The design's second row, less the percentage nothing measures. */
export const RESUMES_IF_INTERRUPTED = "resumes if interrupted";

/** Said when this handset is holding nothing to upload, which is not a failure. */
export const NOTHING_TO_UPLOAD_TITLE = "There's nothing waiting to upload";
export const NOTHING_TO_UPLOAD_SUB =
  "Photos you take are kept on this phone until they're sent. None are waiting right now.";

/**
 * How far one queued capture has got, read from the record and from the record only.
 *
 * Three states, from the two fields `QueuedUpload` carries for exactly this:
 *
 *   - `held` — the record exists and no `uploadId` has been minted, so nothing has been said to
 *     FIL-02. The design's `Waiting`.
 *   - `sending` — FIL-02 has minted the upload and the bytes have not reached the object store.
 *     The design's second row.
 *   - `sent` — the bytes are in. The design's `Uploaded`.
 *
 * **Whether this session still holds the photograph is deliberately not one of them**, and that is
 * the one judgment in this module worth arguing with. It is a different axis: the three states
 * above say how far an upload got, and the bytes say whether *this* session can carry it further.
 * A record that survived a reload has no bytes behind it (Q-73 — they are never persisted), and a
 * row drawn `bad` for that would be reporting a limitation of the handset as a fault of the
 * photograph.
 *
 * `6 Offline queue` already decided where that fact goes: it is a **condition** on the whole
 * screen, with its own chip, `held on phone`. So this screen says it the same way and in the same
 * words — `uploadChip` below takes it — rather than inventing a fourth row status for it. Two
 * screens reading one store must not describe it two ways.
 */
export type CaptureState = "held" | "sending" | "sent";

export function captureState(item: QueuedUpload): CaptureState {
  if (item.written === true) return "sent";
  return item.uploadId === undefined ? "held" : "sending";
}
/**
 * `uploading 2 of 3` — the design's chip, from this handset's own queue.
 *
 * `2` is the 1-based position of the first capture that has not landed, so three captures of which
 * one is in means `uploading 2 of 3`, which is the design's own sample read the only way it can be.
 * The design's class is `unknown` — a dashed pill carrying a `?` — and that is the design's own
 * reading rather than this build's: an upload in flight is a thing nobody has confirmed yet.
 *
 * The two labels the design does not draw take this build's words, and both keep the design's own
 * class: all in is `done`, and a session that no longer holds the photographs is `held on phone`,
 * which is `6 Offline queue`'s word for the same fact about the same store.
 */
export function uploadChip(
  states: readonly CaptureState[],
  /**
   * True when an unfinished capture's photograph is not in this session.
   *
   * `6 Offline queue`'s own condition and its own word for it, because it is the same fact about
   * the same store: the bytes live in memory beside the record and are gone after a reload (Q-73),
   * so the upload cannot be carried further from here however the records read.
   */
  photographsLost = false,
): {
  readonly class: ChipClass;
  readonly label: string;
} {
  const total = states.length;
  const at = states.findIndex((state) => state !== "sent");
  if (at === -1) return { class: "done", label: `${String(total)} of ${String(total)} uploaded` };
  if (photographsLost) return { class: "unknown", label: "held on phone" };
  return { class: "unknown", label: `uploading ${String(at + 1)} of ${String(total)}` };
}
/** True when the chip is in the one state the design draws. */
export function uploadChipIsDesigned(
  states: readonly CaptureState[],
  photographsLost = false,
): boolean {
  return states.length > 0 && !photographsLost && states.some((state) => state !== "sent");
}

/**
 * The design's three check rows, one per capture this handset is holding.
 *
 * The title is the courier's own word for what they photographed — `QueuedUpload.subject`, which
 * `src/offline-queue.ts` keeps for exactly this reason: FIL-02 is never told it, so it is kept where
 * the capture happened rather than asked of a service that does not know.
 *
 * The glyph comes from the status and never from the design's own second array element, which never
 * reaches the page — Q-130. So `ok` is a filled green tick and the two waiting rows are the grey
 * `pending`, which is what the design renders.
 *
 * The `sending` row's sub-line is the design's sentence with the percentage removed rather than
 * approximated. A progress figure composed from a request that reports none would be a number a
 * courier watches and acts on, invented.
 */
export function captureRows(
  captures: readonly { readonly item: QueuedUpload; readonly state: CaptureState }[],
): readonly CheckProps[] {
  return captures.map(({ item, state }) => ({
    key: item.id,
    ...rowFor(state),
    title: item.subject,
  }));
}

function rowFor(state: CaptureState): Omit<CheckProps, "key" | "title"> {
  switch (state) {
    case "sent":
      return { status: "ok", glyph: "check_circle", sub: "Uploaded" };
    case "sending":
      return { status: "wait", glyph: "pending", sub: RESUMES_IF_INTERRUPTED };
    case "held":
      return { status: "wait", glyph: "pending", sub: "Waiting" };
  }
}

/**
 * The note, which is the one thing on this screen the platform answers.
 *
 * `kind` is the design's `pending` — a dashed outline — for every state in which the check has not
 * finished, and `info` once it has, because the blue card is the design's own treatment for a
 * settled fact and the dashed one for something still in the air.
 *
 * Total over FIL-03's six published statuses rather than defaulted, so a status added to that
 * service fails the build here rather than being drawn as the nearest reassuring neighbour. Only
 * `queued` and `processing` carry the design's sentence, because only in those two is "you won't
 * have to redo them" a true statement.
 */
export function blurAndGlareNote(
  job: ProcessingJobStatusRecord | undefined,
  /**
   * True when FIL-03 was asked and did not answer, which is not the same as not being asked.
   *
   * Two sentences rather than one, because a courier can act on one of them and not the other. Not
   * being told which check this is (no `?job=` in the address — Q-142) is this product's own gap and
   * says nothing about the photographs; a refused or unreachable read is a reason to expect the
   * check may have to happen again. Collapsing them would be the console fault R-33 names: two
   * different facts drawn alike.
   */
  unreadable = false,
): {
  readonly kind: "info" | "pending";
  readonly text: string;
  readonly designed: boolean;
} {
  if (job === undefined) {
    return {
      kind: "pending",
      text: unreadable
        ? "We couldn't reach the blur and glare check for these photos, so they may need retaking."
        : "Nothing told this app which blur and glare check these photos are in, so we can't confirm it.",
      designed: false,
    };
  }
  switch (job.status) {
    case "queued":
    case "processing":
      return { kind: "pending", text: BLUR_AND_GLARE_NOTE, designed: true };
    case "retry_wait":
      return {
        kind: "pending",
        text: "The blur and glare check didn't finish and is being tried again. Keep the app open.",
        designed: false,
      };
    case "succeeded":
      return {
        kind: "info",
        text: "These photos passed the blur and glare check, so you won't have to redo them.",
        designed: false,
      };
    case "quarantined":
      return {
        kind: "pending",
        text: "These photos didn't pass the blur and glare check. You'll need to take them again.",
        designed: false,
      };
    case "dead_letter":
      return {
        kind: "pending",
        text: "The blur and glare check gave up on these photos. Support will have to look at them.",
        designed: false,
      };
  }
}

/**
 * Whether FIL-02 says an upload landed, for a capture the queue thinks is still in flight.
 *
 * The queue's `written` flag is this handset's record of its own `PUT` succeeding, and a handset that
 * was killed mid-write has no record of one that did. ADR 0025 published
 * `GET /v1/tenants/{tenantId}/uploads/{uploadId}` so a device can ask, and `quarantined` is FIL-02's
 * own word for "the bytes are in and the safety check has them" — which is this screen's `Uploaded`.
 *
 * `initiated` means minted and not yet written; `expired` and `rejected` are terminal and mean the
 * grant or the object was refused, so the capture has to go again. Total over the four published
 * statuses.
 */
export function landedAccordingToUploadService(upload: UploadRecord | undefined): boolean {
  if (upload === undefined) return false;
  switch (upload.status) {
    case "quarantined":
      return true;
    case "initiated":
    case "expired":
    case "rejected":
      return false;
  }
}
